import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Edit3,
  Activity,
  AlertTriangle,
  X as XIcon,
  Settings as SettingsIcon,
  Bell,
  ChevronsLeft,
  ChevronsRight,
  Factory,
  BrainCircuit,
  Database,
  HelpCircle,
  FileText,
  Zap,
  Cpu,
  MonitorPlay,
  Globe,
  Droplets,
  Users,
  Wrench,
  Bot,
  FileBarChart,
  CircuitBoard,
  ClipboardList,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { sessionStorage } from '../../utils/auth-api';
import { ManualLogging } from './ManualLogging';
import { LivePLCData } from './LivePLCData';
import { OfflineDataViewerScreen } from './screens/OfflineDataViewerScreen';
import { ScrapManagement } from './ScrapManagement';
import { AlarmNotifications } from './AlarmNotifications';
import { MachineEnergyMonitor } from './MachineEnergyMonitor';
import RaiseTicket from './RaiseTicket';
import MyTickets from './MyTickets';
import { HistoricalData } from './HistoricalData';
import { ScannerDashboard } from './ScannerDashboard';
import { HMIViewer } from '../hmi/HMIViewer';
import { WaterPlantMonitor } from './WaterPlantMonitor';
import { PlantKPIDashboard } from './PlantKPIDashboard';
import { MaintenanceBanner } from './MaintenanceBanner';
import { MaintenanceSchedulerPanel } from './MaintenanceSchedulerPanel';
import { TeamActivity } from './TeamActivity';
import { AutomatedReports } from './AutomatedReports';
import { OperationsCopilot } from './OperationsCopilot';
import { ReportBuilder } from './ReportBuilder';
import { EnergyForecast } from './EnergyForecast';
import { ChatBot } from './ChatBot';
import { Waveshare8CHDashboard } from './Waveshare8CHDashboard';
import { BatchReports } from './BatchReports';
import { API_BASE } from '../../utils/api-base';
import { vfdApi } from '../../utils/vfd';
import { controllerApi } from '../../utils/controllers';
import { customScreenLabel } from '../../utils/clientScreens';


interface ClientPortalProps {
  client: any;
  session: any;
  onLogout: () => void;
}

const MODULE_PERMISSION_DEFAULTS = {
  energyConsumption: { view: true, download: false },
  manualLogging: { view: true, edit: true, download: false },
  livePlcData: { view: true, download: false },
  reports: { view: true, summaryReport: false },
  offlineData: { view: true, download: false, sync: true },
  scrapManagement: { view: true, edit: true, download: false },
  supportTickets: { view: true, create: true },
  settings: { view: true, edit: true },  // Everyone can see their own settings
  scanners: { view: true, edit: true, download: false },
  externalMonitor: { view: true },
  hmi: { view: true },
  plantKpi: { view: true },
  waveshare8ch: { view: true },
} as const;

const buildDisabledPermissions = (template: Record<string, boolean>) => (
  Object.fromEntries(Object.keys(template).map((key) => [key, false]))
);

const normalizeModulePermissions = (
  moduleKey: keyof typeof MODULE_PERMISSION_DEFAULTS,
  rawPermissions: any,
  clientModules: any,
  role: string
) => {
  const base = MODULE_PERMISSION_DEFAULTS[moduleKey];
  const moduleEnabled = clientModules?.[moduleKey] !== false;
  const elevatedRole = role === 'admin' || role === 'client_admin' || role === 'super_admin';

  if (!moduleEnabled) {
    return buildDisabledPermissions(base);
  }

  if (rawPermissions?.all === true) {
    return Object.fromEntries(Object.keys(base).map((key) => [key, true]));
  }

  const source = rawPermissions?.[moduleKey];
  if (typeof source === 'boolean') {
    if (!source) {
      return buildDisabledPermissions(base);
    }
    if (elevatedRole) {
      return Object.fromEntries(Object.keys(base).map((key) => [key, true]));
    }
    return {
      ...base,
      view: true,
    };
  }

  const merged = {
    ...base,
    ...(source && typeof source === 'object' ? source : {}),
  };

  const normalized = Object.fromEntries(
    Object.entries(merged).map(([key, value]) => [key, !!value])
  ) as Record<string, boolean>;

  if (elevatedRole) {
    return Object.fromEntries(Object.keys(normalized).map((key) => [key, true]));
  }

  return normalized;
};

// Helper to convert hex color to RGB for CSS variables
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '59, 130, 246'; // Fallback blue RGB
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
};

export function ClientPortal({ client, session, onLogout }: ClientPortalProps) {
  const { theme, toggleTheme } = useTheme();
  const portalRef = useRef<HTMLDivElement>(null);
  const styleElementRef = useRef<HTMLStyleElement | null>(null);
  
  const themeConfig = client?.themeConfig || {
    primaryColor: '#3b82f6',
    sidebarStyle: 'default',
    dashboardStyle: 'modern'
  };

  // Inject dynamic styles ONLY to this client portal (scoped, not global)
  useEffect(() => {
    if (themeConfig.primaryColor && portalRef.current) {
      console.log('🎨 Applying client-specific theme color:', themeConfig.primaryColor);
      
      // Remove old style element if it exists
      if (styleElementRef.current) {
        styleElementRef.current.remove();
      }

      // Create a new scoped style element for this portal only
      const style = document.createElement('style');
      const rgbColor = hexToRgb(themeConfig.primaryColor);
      
      // Apply styles ONLY to elements within this portal container
      style.textContent = `
        [data-client-portal] button,
        [data-client-portal] [role="button"],
        [data-client-portal] a {
          --primary: ${themeConfig.primaryColor};
          --primary-rgb: ${rgbColor};
        }
        [data-client-portal] {
          --primary: ${themeConfig.primaryColor};
          --primary-rgb: ${rgbColor};
        }
      `;
      
      document.head.appendChild(style);
      styleElementRef.current = style;

      // Also apply inline styles to portal container
      if (portalRef.current) {
        portalRef.current.style.setProperty('--primary', themeConfig.primaryColor);
        portalRef.current.style.setProperty('--primary-rgb', rgbColor);
      }

      return () => {
        if (styleElementRef.current) {
          styleElementRef.current.remove();
          styleElementRef.current = null;
        }
      };
    }
  }, [themeConfig.primaryColor]);
  const [activeScreen, setActiveScreen] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth >= 1024;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState<any>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseBannerDismissed, setLicenseBannerDismissed] = useState(false);
  const factreonLogoSrc = './img/logo.png';
  const sidebarFooterLogoSrc = './img/waterwalk.png';
  const [clientLogoFailed, setClientLogoFailed] = useState(false);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const licenseToastShownRef = useRef(false); // Track if license toast was shown
  const firstRenderRef = useRef(true); // Track first render to skip animations

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Cancel any pending requests
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setClientLogoFailed(false);
  }, [client?.logo]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncSidebarState = (event?: MediaQueryListEvent) => {
      const matches = event?.matches ?? mediaQuery.matches;
      setSidebarOpen(matches);
      if (!matches) {
        setSidebarCollapsed(false);
      }
    };

    syncSidebarState();
    mediaQuery.addEventListener('change', syncSidebarState);
    return () => mediaQuery.removeEventListener('change', syncSidebarState);
  }, []);

  // Presence heartbeat — lets the Team Activity screen show real-time online/offline
  // status (see backend touchUserPresence/sweepUserPresence). Pings while this tab is open.
  useEffect(() => {
    if (!session?.token) return;
    const ping = () => {
      fetch(`${API_BASE}/api/users/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.token}` },
      }).catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 30000);
    return () => clearInterval(interval);
  }, [session?.token]);

  // Show license notification ONLY ONCE on mount
  useEffect(() => {
    if (licenseToastShownRef.current) return; // Already shown
    
    const days = session?.daysUntilExpiration;
    const expDate = session?.licenseExpiresAt
      ? new Date(session.licenseExpiresAt).toLocaleDateString()
      : '';

    if (days !== undefined && days !== null && days <= 30 && days > 0) {
      licenseToastShownRef.current = true;
      
      if (days === 1) {
        toast.warning(
          `⚠️ Your license expires tomorrow (${expDate})`,
          {
            description: 'Please contact your Super Administrator to renew your license.',
            duration: 10000,
            position: 'top-right',
          }
        );
      } else {
        toast.info(
          `ℹ️ License expires in ${days} days (${expDate})`,
          {
            description: 'Contact your administrator to schedule a renewal.',
            duration: 8000,
            position: 'top-right',
          }
        );
      }
    }
  }, []); // Run ONLY on mount

  const clientInitials = (client?.name || 'CL')
    .split(' ')
    .map((part: string) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const userRole = session.user?.role || session.role || 'operator';
  const rawPermissions = session.user?.permissions || session.permissions || {};
  const permissions = useMemo(() => {
    const alarmPermissions = rawPermissions?.alarmNotifications;
    const alarmNotificationsView = rawPermissions?.all === true
      ? true
      : !!(alarmPermissions && typeof alarmPermissions === 'object'
        ? alarmPermissions.view
        : alarmPermissions);

    return {
      energyConsumption: normalizeModulePermissions('energyConsumption', rawPermissions, client?.modules, userRole),
      manualLogging: normalizeModulePermissions('manualLogging', rawPermissions, client?.modules, userRole),
      livePlcData: normalizeModulePermissions('livePlcData', rawPermissions, client?.modules, userRole),
      reports: normalizeModulePermissions('reports', rawPermissions, client?.modules, userRole),
      offlineData: normalizeModulePermissions('offlineData', rawPermissions, client?.modules, userRole),
      scrapManagement: normalizeModulePermissions('scrapManagement', rawPermissions, client?.modules, userRole),
      settings: normalizeModulePermissions('settings', rawPermissions, client?.modules, userRole),
      supportTickets: normalizeModulePermissions('supportTickets', rawPermissions, client?.modules, userRole),
      scanners: normalizeModulePermissions('scanners', rawPermissions, client?.modules, userRole),
      externalMonitor: normalizeModulePermissions('externalMonitor', rawPermissions, client?.modules, userRole),
      hmi: normalizeModulePermissions('hmi', rawPermissions, client?.modules, userRole),
      plantKpi: normalizeModulePermissions('plantKpi', rawPermissions, client?.modules, userRole),
      waveshare8ch: normalizeModulePermissions('waveshare8ch', rawPermissions, client?.modules, userRole),
      alarmNotifications: { view: alarmNotificationsView },
    };
  }, [rawPermissions, client?.modules, userRole]);

  // "Live PLC Data" also hosts the VFD Drives and Controllers tabs, so it is hidden only
  // for a client with no PLC, VFD and controller at all. null = not known yet (shown).
  const [hasLiveDevices, setHasLiveDevices] = useState<boolean | null>(null);
  useEffect(() => {
    if (!client?.id || !session?.token) return;
    let cancelled = false;
    const token = session.token;
    const count = (p: Promise<any[]>) => p.then((l) => l.length).catch(() => null);
    Promise.all([
      fetch(`${API_BASE}/api/clients/${client.id}/plcs`, { headers: { Authorization: `Bearer ${token}` } })
        .then(async (res) => {
          if (!res.ok) return null;
          const body = await res.json().catch(() => ({}));
          return Array.isArray(body) ? body.length : (Array.isArray(body?.plcs) ? body.plcs.length : null);
        })
        .catch(() => null),
      count(vfdApi.listForClient(token, client.id)),
      count(controllerApi.listForClient(token, client.id)),
    ]).then((counts) => {
      if (cancelled) return;
      // Any lookup that failed counts as "maybe", so a hiccup never hides a working screen.
      setHasLiveDevices(counts.some((n) => n === null || n > 0));
    });
    return () => { cancelled = true; };
  }, [client?.id, session?.token]);

  // Available screens based on permissions
  const screens = useMemo(() => [
    {
      id: 'machine',
      label: 'Energy Meters',
      icon: Zap,
      enabled: permissions.energyConsumption?.view,
    },
    {
      id: 'energy-forecast',
      label: 'Energy Forecast',
      icon: Zap,
      enabled: !!(permissions.energyConsumption?.view && client?.modules?.energyForecast === true),
    },
    {
      id: 'chat-bot',
      label: 'Chat Bot',
      icon: Bot,
      enabled: client?.modules?.chatBot === true,
    },
    {
      id: 'scanners',
      label: 'Scanners',
      icon: Cpu,
      enabled: permissions.scanners?.view,
    },
    {
      id: 'waveshare-8ch',
      label: 'Waveshare 8CH',
      icon: CircuitBoard,
      enabled: permissions.waveshare8ch?.view,
    },
    {
      id: 'logging',
      label: 'Manual Logging',
      icon: Edit3,
      enabled: permissions.manualLogging?.view,
    },
    {
      id: 'plc',
      label: 'Live PLC Data',
      icon: Activity,
      enabled: permissions.livePlcData?.view && hasLiveDevices !== false,
    },
    {
      id: 'offline',
      label: 'Offline Data',
      icon: Database as any,
      enabled: permissions.offlineData?.view,
    },
    {
      id: 'scrap',
      label: 'Scrap Management',
      icon: AlertTriangle,
      enabled: permissions.scrapManagement?.view,
    },
    {
      id: 'raise-ticket',
      label: 'Raise Ticket',
      icon: HelpCircle,
      enabled: permissions.supportTickets?.create,
    },
    {
      id: 'my-tickets',
      label: 'My Tickets',
      icon: HelpCircle,
      enabled: permissions.supportTickets?.view,
    },
    {
      id: 'historical',
      label: 'Reports',
      icon: FileText,
      enabled: permissions.reports?.view,
    },
    {
      id: 'hmi',
      label: 'HMI',
      icon: MonitorPlay,
      enabled: permissions.hmi?.view,
    },
    {
      id: 'external-monitor',
      label: 'Monitor',
      icon: Globe,
      enabled: !!(permissions.externalMonitor?.view),
    },
    {
      id: 'plant-kpi',
      label: 'Plant KPIs',
      icon: Droplets,
      enabled: !!(permissions.plantKpi?.view),
    },
    {
      id: 'maintenance-scheduler',
      label: 'Maintenance Scheduler',
      icon: Wrench,
      enabled: ['admin', 'supervisor', 'client_admin', 'super_admin'].includes(userRole) && client?.modules?.maintenanceScheduler === true,
    },
    {
      id: 'team-activity',
      label: 'Team Activity',
      icon: Users,
      enabled: ['admin', 'supervisor', 'client_admin', 'super_admin'].includes(userRole) && client?.modules?.teamActivity === true,
    },
    {
      id: 'automated-reports',
      label: 'Automated Reports',
      icon: Bell,
      enabled: ['admin', 'supervisor', 'client_admin', 'super_admin'].includes(userRole) && client?.modules?.automatedReports === true,
    },
    {
      id: 'report-builder',
      label: 'Report Builder',
      icon: FileBarChart,
      enabled: ['admin', 'supervisor', 'client_admin', 'super_admin'].includes(userRole) && client?.modules?.automatedReports === true,
    },
    {
      id: 'batch-reports',
      label: 'Batch Reports',
      icon: ClipboardList,
      enabled: client?.modules?.batchReports === true,
    },
    {
      id: 'copilot',
      label: 'Operations Copilot',
      icon: Bot,
      enabled: client?.modules?.copilot === true,
    },
  ].filter((screen) => screen.enabled)
    // Each client can rename its sidebar screens (Admin → Screen Names).
    .map((screen) => ({ ...screen, label: customScreenLabel(client?.themeConfig, screen.id) || screen.label })),
  [permissions, client?.modules, client?.themeConfig, userRole, hasLiveDevices]);

  // Get array of enabled screen IDs
  const enabledScreenIds = useMemo(() => screens.map((s) => s.id), [screens]);

  // Prevent viewing disabled screens - redirect if activeScreen is not enabled
  useEffect(() => {
    if (screens.length === 0) {
      setActiveScreen('');
      return;
    }

    if (!activeScreen || !enabledScreenIds.includes(activeScreen)) {
      setActiveScreen(screens[0].id);
    }
  }, [enabledScreenIds, activeScreen, screens]);

  const handleScreenSelect = (screenId: string) => {
    setActiveScreen(screenId);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" ref={portalRef} data-client-portal>
      {/* License Expiration Banner — dismissible, only shown for warning/expiring-soon */}
      {licenseInfo && !licenseBannerDismissed && (
        <>
          {(licenseInfo.status === 'warning' || licenseInfo.status === 'expiring-soon') && licenseInfo.daysUntilExpiration > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="bg-yellow-50 dark:bg-yellow-950/30 border-b-2 border-yellow-500 px-6 py-3"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-yellow-900 dark:text-yellow-200">
                    Your license expires in {licenseInfo.daysUntilExpiration} day{licenseInfo.daysUntilExpiration === 1 ? '' : 's'}
                  </p>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Expiration date: {new Date(licenseInfo.expiresAt).toLocaleDateString()} &bull; Contact your Super Administrator to renew.
                  </p>
                </div>
                <button
                  onClick={() => setLicenseBannerDismissed(true)}
                  className="p-1 rounded hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors shrink-0"
                  aria-label="Dismiss license warning"
                >
                  <XIcon className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
                </button>
              </div>
            </motion.div>
          )}
        </>
      )}

      {client?.modules?.maintenanceScheduler === true && (
        <MaintenanceBanner clientId={client.id} token={session.token} />
      )}

      <div className="flex min-w-0">
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm lg:hidden"
          />
        )}

        {/* Sidebar Navigation */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200, duration: 0.2 }}
              className={`fixed top-0 h-screen border-r z-50 transition-all duration-300 ${
                sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
              } ${
                themeConfig.sidebarStyle === 'minimal' ? 'bg-background' : 
                themeConfig.sidebarStyle === 'dark' ? 'bg-slate-950 text-white border-slate-800' :
                themeConfig.sidebarStyle === 'glass' ? 'bg-card/30 backdrop-blur-xl border-white/10' :
                'bg-card'
              } w-[85vw] max-w-64 lg:sticky lg:max-w-none flex flex-col`}
            >
              <Button
                variant="secondary"
                size="icon"
                onClick={() => setSidebarCollapsed((prev) => !prev)}
                className="absolute top-1/2 -right-3 hidden h-8 w-8 -translate-y-1/2 rounded-full border shadow-sm lg:flex"
              >
                {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
              </Button>
              <div className="border-b border-border/40 p-4 pt-20 lg:pt-4">
                {sidebarCollapsed ? (
                  <div className="flex flex-col items-center gap-2 py-2">
                    <div className="flex items-center justify-center shrink-0 w-20 h-20">
                      <img
                        src={factreonLogoSrc}
                        alt="Factreon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="relative flex items-center justify-center" style={{ height: '112px' }}>
                    <div className="flex items-center justify-center shrink-0 w-60 h-24">
                      <img
                        src={factreonLogoSrc}
                        alt="Factreon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>

              <nav className={`space-y-2 p-4 ${sidebarCollapsed ? 'lg:px-2' : ''} flex-1 overflow-y-auto`}>
                {screens.map((screen, index) => {
                  const Icon = screen.icon;
                  const isActive = activeScreen === screen.id;

                  return (
                    <motion.button
                      key={screen.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03, duration: 0.15 }}
                      onClick={() => handleScreenSelect(screen.id)}
                      className={`w-full flex items-start text-left ${sidebarCollapsed ? 'lg:justify-center' : 'gap-3'} justify-start px-4 py-3 rounded-lg transition-all relative ${isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
                      {(!sidebarCollapsed || (typeof window !== 'undefined' && window.innerWidth < 1024)) && <span className="font-medium text-left leading-snug flex-1 min-w-0">{screen.label}</span>}
                      {isActive && !sidebarCollapsed && (
                        <motion.div
                          layoutId="activeScreenIndicator"
                          className={`ml-auto self-center w-2 h-2 rounded-full shrink-0 ${
                            themeConfig.sidebarStyle === 'dark' ? 'bg-primary' : 'bg-white'
                          }`}
                        />
                      )}
                      {isActive && sidebarCollapsed && (
                        <div className={`absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-8 rounded-l-full ${
                          themeConfig.sidebarStyle === 'dark' ? 'bg-primary' : 'bg-white'
                        }`} />
                      )}
                    </motion.button>
                  );
                })}
              </nav>

              <div className={`border-t border-border/40 p-3 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
                <div className={`rounded-lg bg-muted/20 ${sidebarCollapsed ? 'p-2' : 'p-2.5'} flex items-center justify-center`}>
                  <img
                    src={sidebarFooterLogoSrc}
                    alt="Sidebar footer logo"
                    className={`${sidebarCollapsed ? 'h-12 w-12' : 'h-20 w-20'} object-contain`}
                    style={{ imageRendering: 'auto', transform: 'translateZ(0)' }}
                    decoding="async"
                    onError={(event) => {
                      const img = event.currentTarget;
                      if (img.dataset.fallbackApplied === 'true') return;
                      img.dataset.fallbackApplied = 'true';
                      img.src = factreonLogoSrc;
                    }}
                  />
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        <div className={`min-w-0 flex-1 flex flex-col ${
          themeConfig.dashboardStyle === 'compact' ? 'max-w-[1600px] mx-auto' : ''
        }`}>
          {/* Header */}
          <motion.header
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="sticky top-0 z-40 border-b bg-card shadow-sm"
          >
            <div className="relative flex min-h-20 flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:h-20 lg:flex-nowrap lg:px-8 lg:py-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="shrink-0 lg:hidden"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>

              <div className="flex min-w-0 flex-1 items-center justify-center lg:absolute lg:left-1/2 lg:top-1/2 lg:min-w-fit lg:-translate-x-1/2 lg:-translate-y-1/2 lg:flex-none">
                {client.logo && !clientLogoFailed ? (
                  <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="h-auto max-w-[140px] object-contain sm:max-w-[220px]"
                      style={{
                        height: `${Math.max(Number(client.logoSize) || 64, 40)}px`,
                        width: 'auto',
                      }}
                      onError={() => setClientLogoFailed(true)}
                    />
                    <span className="max-w-[140px] truncate text-sm font-semibold text-foreground sm:max-w-[220px] sm:text-base">
                      {client.name}
                    </span>
                  </div>
                ) : (
                  <div className="flex min-w-0 items-center gap-2 rounded-lg border border-blue-500/20 bg-gradient-to-r from-blue-500/10 to-purple-600/10 px-2 py-1.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 sm:h-10 sm:w-10">
                      <span className="text-white text-base font-bold">{clientInitials}</span>
                    </div>
                    <span className="truncate text-sm font-semibold text-foreground sm:text-lg">{client.name}</span>
                  </div>
                )}
              </div>

              <div className="ml-auto flex items-center gap-2 sm:gap-3">
                {/* Watermark badge */}
                <div className="hidden xl:flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5">
                  <span className="text-[11px] font-semibold tracking-wide text-muted-foreground/70">A Waterwalk Creation</span>
                </div>

                {client?.modules?.smartAlerts === true && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="shrink-0 transition-all hover:scale-110 hover:bg-accent"
                  >
                    <Bell className="h-5 w-5" />
                  </Button>
                )}

                <Button variant="ghost" size="icon" onClick={toggleTheme} className="shrink-0 transition-all hover:scale-110 hover:bg-accent">
                  {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>

                <Button
                  variant="destructive"
                  onClick={onLogout}
                  size="sm"
                  className="gap-2 shrink-0 px-3 py-2 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:opacity-90 sm:px-4"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </motion.header>

          {/* Content Area */}
          <main className="smooth-scroll min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-3 sm:p-6 lg:p-8">
            {/* The next screen mounts at once (no waiting for the old one to fade out). */}
            <div
              key={activeScreen}
              className={`screen-enter ${activeScreen === 'hmi' ? 'min-w-0 h-[calc(100vh-160px)]' : 'min-w-0'}`}
            >
                {screens.length === 0 && (
                  <div className="text-center py-12">
                    <SettingsIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No Modules Enabled</h3>
                    <p className="text-muted-foreground">
                      Your account does not have access to any dashboard modules. Contact your administrator.
                    </p>
                  </div>
                )}
                {activeScreen === 'logging' && (
                  <ManualLogging
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                  />
                )}
                {activeScreen === 'plc' && (
                  <LivePLCData
                    client={client}
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                  />
                )}
                {activeScreen === 'historical' && (
                  <HistoricalData
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                  />
                )}
                {activeScreen === 'offline' && (
                  <OfflineDataViewerScreen clientId={client.id} token={session.token} />
                )}
                {activeScreen === 'machine' && (
                  <MachineEnergyMonitor
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                    userRole={userRole}
                    pollingIntervalMs={client.pollingIntervalMs}
                    onOpenDetailedReport={() => setActiveScreen('historical')}
                  />
                )}
                {activeScreen === 'scanners' && (
                  <ScannerDashboard
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                    pollingIntervalMs={client.pollingIntervalMs}
                  />
                )}
                {activeScreen === 'waveshare-8ch' && (
                  <Waveshare8CHDashboard
                    clientId={client.id}
                    token={session.token}
                    pollingIntervalMs={client.pollingIntervalMs}
                    showPanelDrawing={client.modules?.ioPanelDrawing !== false}
                  />
                )}
                {activeScreen === 'hmi' && (
                  <HMIViewer
                    clientId={client.id}
                    token={session.token}
                    pollingIntervalMs={client.pollingIntervalMs}
                  />
                )}
                {activeScreen === 'external-monitor' && (
                  <WaterPlantMonitor />
                )}
                {activeScreen === 'plant-kpi' && (
                  <PlantKPIDashboard
                    clientId={client.id}
                    token={session.token}
                    pollingIntervalMs={client.pollingIntervalMs}
                  />
                )}
                {activeScreen === 'scrap' && (
                  <ScrapManagement
                    clientId={client.id}
                    token={session.token}
                    permissions={permissions}
                  />
                )}
                {activeScreen === 'raise-ticket' && (
                  <RaiseTicket
                    clientId={client.id}
                    token={session.token}
                    userEmail={session.user?.email || ''}
                    companyName={client.name || ''}
                  />
                )}
                {activeScreen === 'my-tickets' && (
                  <MyTickets
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'maintenance-scheduler' && (
                  <MaintenanceSchedulerPanel
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'team-activity' && (
                  <TeamActivity
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'automated-reports' && (
                  <AutomatedReports
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'copilot' && (
                  <OperationsCopilot
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'report-builder' && (
                  <ReportBuilder
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'batch-reports' && (
                  <BatchReports
                    clientId={client.id}
                    token={session.token}
                    userRole={userRole}
                  />
                )}
                {activeScreen === 'energy-forecast' && (
                  <EnergyForecast
                    clientId={client.id}
                    token={session.token}
                  />
                )}
                {activeScreen === 'chat-bot' && (
                  <ChatBot
                    clientId={client.id}
                    token={session.token}
                  />
                )}
            </div>
          </main>

          {/* Notifications Panel */}
          <AnimatePresence>
            {showNotifications && permissions.alarmNotifications?.view && client?.modules?.smartAlerts === true && (
              <motion.div
                initial={{ x: 400 }}
                animate={{ x: 0 }}
                exit={{ x: 400 }}
                transition={{ type: "spring", damping: 20 }}
                className="fixed right-0 top-20 z-40 h-[calc(100vh-80px)] w-full max-w-full border-l bg-card/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:w-96"
              >
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="font-semibold text-lg">Alarm Notifications</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNotifications(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <div className="p-4">
                  <AlarmNotifications
                    clientId={client.id}
                    token={session.token}
                    username={session.user?.name || session.username}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
